import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx"; 
import { Spin } from 'components/shared/AntComponent';
import dayjs from 'dayjs';

const BAND_MAPPING: Record<string, { start: number; end: number; color: string }> = {
  Delta: { start: 1, end: 4, color: "#3b82f6" },
  Theta: { start: 5, end: 8, color: "#06b6d4" },
  Alpha: { start: 9, end: 12, color: "#10b981" },
  Beta: { start: 13, end: 20, color: "#f59e0b" },
  "High Beta": { start: 21, end: 30, color: "#ef4444" },
};

const REQUIRED_CHANNELS = ["Fz", "O1", "O2"];

const brainElectrodesCoords = [
  { channel: "Fp1", x: 165, y: 70 },  { channel: "Fp2", x: 235, y: 70 },
  { channel: "F7",  x: 90,  y: 130 }, { channel: "F3",  x: 145, y: 130 },
  { channel: "Fz",  x: 200, y: 130 }, { channel: "F4",  x: 255, y: 130 },
  { channel: "F8",  x: 310, y: 130 }, 
  { channel: "T3",  x: 75,  y: 200 }, { channel: "C3",  x: 140, y: 200 },
  { channel: "Cz",  x: 200, y: 200 }, { channel: "C4",  x: 260, y: 200 },
  { channel: "T4",  x: 325, y: 200 }, 
  { channel: "T5",  x: 95,  y: 275 }, { channel: "P3",  x: 145, y: 275 },
  { channel: "Pz",  x: 200, y: 275 }, { channel: "P4",  x: 255, y: 275 },
  { channel: "T6",  x: 305, y: 275 }, 
  { channel: "O1",  x: 160, y: 340 }, { channel: "O2",  x: 240, y: 340 }
];

interface NewResultComparisonProps {
  preExcel?: any;
  postExcel?: any;
  preExcelEO?: any;
  postExcelEO?: any;
  compareInfo?: any[];
}

interface ParsedExcelData {
  relative: Record<string, any>;
  absolute: Record<string, any>;
  alphaPeak: Record<string, any>;
  ratios: Record<string, any>;
  coherence: any[];
  amplitudeAsymmetry: any[];
}

interface AmplitudeAsymmetryEntry {
  pair: string;
  ch1: string;
  ch2: string;
  pre: number;
  post: number;
  delta: number;
  pctChange: number;
  flip: boolean;
}

const NewResultComparison = ({
  preExcel,
  postExcel,
  preExcelEO,
  postExcelEO,
  compareInfo = [],
}: NewResultComparisonProps) => {
  const [eyeState, setEyeState] = useState("ec");
  const [activeBand, setActiveBand] = useState("Alpha"); 
const [isDownloading, setIsDownloading] = useState(false);
  const [frequencyThreshold, setFrequencyThreshold] = useState(0.1);
  const [spectralTab, setSpectralTab] = useState("ratios");
  const [absolutePowerChannel, setAbsolutePowerChannel] = useState("Fp1");
  
  const [perHzAbsoluteChannel, setPerHzAbsoluteChannel] = useState("Cz");
  const [perHzRelativeChannel, setPerHzRelativeChannel] = useState("Cz");

  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [preFile, setPreFile] = useState<File | null>(null);
  const [postFile, setPostFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [isProcessed, setIsProcessed] = useState(false);
  const [preCalculatedData, setPreCalculatedData] = useState<ParsedExcelData | null>(null);
  const [postCalculatedData, setPostCalculatedData] = useState<ParsedExcelData | null>(null);
  
  const [dynamicAlphaPeakData, setDynamicAlphaPeakData] = useState<{
    channel: string;
    before: number;
    after: number;
    shift: number;
    x: number;
    y: number;
  }[]>([]);
  const [dynamicSpectralRatios, setDynamicSpectralRatios] = useState<any[]>([]);
  const [dynamicRelativePower, setDynamicRelativePower] = useState<Record<string, any>>({});
  const [perHzViewMode, setPerHzViewMode] = useState("absolute");

  const [coherenceThreshold, setCoherenceThreshold] = useState(15);
  const [showAllCoherencePairs, setShowAllCoherencePairs] = useState(true);
  const [selectedCoherencePair, setSelectedCoherencePair] = useState<string | null>(null);
  const [flagThreshold, setFlagThreshold] = useState(20);
  const [ratioThreshold, setRatioThreshold] = useState(25);

  const parseExcelFile = (file: File): Promise<ParsedExcelData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          if (!e.target?.result || typeof e.target.result === 'string') {
            reject(new Error("Invalid file result"));
            return;
          }
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const parsedData: ParsedExcelData = { relative: {}, absolute: {}, alphaPeak: {}, ratios: {}, coherence: [], amplitudeAsymmetry: [] };
          
          const sheetNames = workbook.SheetNames;
          const relativeSheetName = sheetNames.find(n => n.replace(/\s+/g, "").toLowerCase() === "relativepower");
          const absoluteSheetName = sheetNames.find(n => n.replace(/\s+/g, "").toLowerCase() === "absolutepower");
          const alphaPeakSheetName = sheetNames.find(n => n.replace(/\s+/g, "").toLowerCase().includes("alphapeakfrequency"));
          const ratiosSheetName = sheetNames.find(n => n.replace(/\s+/g, "").toLowerCase() === "ratios");
          const coherenceSheetName = sheetNames.find(n => n.replace(/\s+/g, "").toLowerCase().includes("coherence"));
          const amplitudeSheetName = sheetNames.find(n => n.replace(/\s+/g, "").toLowerCase().includes("amplitudeasymmetry"));

         const extractSheet = (sheetName: string | undefined, targetKey: 'relative' | 'absolute' | 'alphaPeak' | 'ratios') => {
    if (!sheetName) return;
    const sheet = workbook.Sheets[sheetName];
    const json = XLSX.utils.sheet_to_json<Record<string, any>>(sheet);

    if (!json || json.length === 0) {
        console.warn(`Excel layout matching elements missing inside sheet named: ${sheetName}`);
        return;
    }

    json.forEach((row) => {
        if (!row) return; 
        const channelKey = Object.keys(row).find(
            (k) => k.toLowerCase().startsWith("channel") || k.toLowerCase().startsWith("channels")
        );
        if (channelKey) {
            const channelName = String(row[channelKey]).trim();
            parsedData[targetKey][channelName] = row;
        }
    });
};

          extractSheet(relativeSheetName, "relative");
          extractSheet(absoluteSheetName, "absolute");
          extractSheet(alphaPeakSheetName, "alphaPeak");
          extractSheet(ratiosSheetName, "ratios");

          if (coherenceSheetName) {
            const coherenceSheet = workbook.Sheets[coherenceSheetName];
            parsedData.coherence = XLSX.utils.sheet_to_json(coherenceSheet);
          }
          if (amplitudeSheetName) {
            const amplitudeSheet = workbook.Sheets[amplitudeSheetName];
            parsedData.amplitudeAsymmetry = XLSX.utils.sheet_to_json(amplitudeSheet);
          }
          
          resolve(parsedData);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("File processing error"));
      reader.readAsArrayBuffer(file);
    });
  };

const loadExcelFiles = async () => {
    try {
        setIsProcessed(false);
        setIsDownloading(true); // Start loading
        setUploadError("");
        
        const targetPre = eyeState === "eo" ? preExcelEO : preExcel;
        const targetPost = eyeState === "eo" ? postExcelEO : postExcel;

        if (!targetPre?.output_file_url || !targetPost?.output_file_url) {
            setIsDownloading(false);
            return;
        }

        const pre = await fetchExcel(targetPre.output_file_url);
        const post = await fetchExcel(targetPost.output_file_url);
        
        if (!pre || !post) {
            setUploadError("Cloud storage connection refused. Failed to parse downloaded stream files.");
            setIsDownloading(false);
            return;
        }

        setPreFile(pre);
        setPostFile(post);
        await handleProcessScans(pre, post);
    } catch (err) {
        setUploadError("Error reading array content from server layers.");
        console.error("Error downloading excel raw files:", err);
    } finally {
        setIsDownloading(false); // Stop loading
    }
  };

useEffect(() => {
    loadExcelFiles();
}, [preExcel?.output_file_url, postExcel?.output_file_url, preExcelEO?.output_file_url, postExcelEO?.output_file_url, eyeState]);

const fetchExcel = async (url: string) => {
    try {
        const response = await fetch(url, {
            method: 'GET',
            mode: 'cors', 
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
        });
        
        if (!response.ok) {
            throw new Error(`Server returned error code status context: ${response.status}`);
        }
        
        const blob = await response.blob();
        const extractedFilename = url.split("?")[0].split("/").pop() || "raw_eeg_report.xlsx";
        
        return new File([blob], extractedFilename, { type: blob.type });
    } catch (error) {
        console.error("Network streaming read connection crashed:", error);
        return null;
    }
};

  const getChannels = (row: any) => {
    const keys = Object.keys(row);
    const ch1Key = keys.find(k => ["channel1", "channels1", "ch1", "electrode1"].includes(k.replace(/\s+/g, "").toLowerCase()));
    const ch2Key = keys.find(k => ["channel2", "channels2", "ch2", "electrode2"].includes(k.replace(/\s+/g, "").toLowerCase()));
    return {
      ch1: ch1Key ? String(row[ch1Key]).trim() : "",
      ch2: ch2Key ? String(row[ch2Key]).trim() : ""
    };
  };

  const rawAmplitudeAsymmetry = useMemo<AmplitudeAsymmetryEntry[]>(() => {
    if (!isProcessed || !preCalculatedData || !postCalculatedData) return [];

    const preRows = preCalculatedData.amplitudeAsymmetry || [];
    const postRows = postCalculatedData.amplitudeAsymmetry || [];

    const getBandAverage = (row: any) => {
      const range = BAND_MAPPING[activeBand];
      if (!range) return 0;
      let sum = 0;
      let count = 0;
      for (let hz = range.start; hz <= range.end; hz++) {
        const key = `${hz}Hz`;
        if (row[key] !== undefined) {
          sum += parseFloat(row[key]) || 0;
          count++;
        }
      }
      return count ? sum / count : 0;
    };

    return preRows.map(preRow => {
      const { ch1, ch2 } = getChannels(preRow);
      const postRow = postRows.find(r => {
        const p = getChannels(r);
        return ((p.ch1 === ch1 && p.ch2 === ch2) || (p.ch1 === ch2 && p.ch2 === ch1));
      });

      if (!postRow) return null;

      const pre = getBandAverage(preRow);
      const post = getBandAverage(postRow);
      const delta = post - pre;
      const pctChange = pre !== 0 ? Math.abs((delta / pre) * 100) : Math.abs(delta * 100);

      return {
        pair: `${ch1} ↔ ${ch2}`,
        ch1, ch2, pre, post, delta, pctChange,
        flip: Math.sign(pre) !== Math.sign(post)
      };
    }).filter((item): item is AmplitudeAsymmetryEntry => item !== null);
  }, [isProcessed, preCalculatedData, postCalculatedData, activeBand]);

  const dynamicAmplitudeAsymmetry = useMemo<AmplitudeAsymmetryEntry[]>(() => {
    return rawAmplitudeAsymmetry.filter((item) => item.pctChange >= flagThreshold);
  }, [rawAmplitudeAsymmetry, flagThreshold]);

  const getPositionPercent = (val: number) => {
    const absVal = Math.min(Math.abs(val), 1);
    return (absVal * 100).toFixed(1) + "%";
  };

  const isFlipped = (pre: number, post: number) => (pre < 0 && post > 0) || (pre > 0 && post < 0);

  const getRegionFromChannel = (channelName: string) => {
    if (!channelName) return "Other";
    const firstChar = channelName.trim().charAt(0).toUpperCase();
    switch (firstChar) {
      case "F": return "Frontal";
      case "C": return "Central";
      case "T": return "Temporal";
      case "P": return "Parietal";
      case "O": return "Occipital";
      default: return "Other";
    }
  };

  const calculateDynamicRelativeComposition = (preData: ParsedExcelData, postData: ParsedExcelData) => {
    const preRel = preData.relative || {};
    const postRel = postData.relative || {};
    const allChannels = Array.from(new Set([...Object.keys(preRel), ...Object.keys(postRel)]));
    const regions: Record<string, any[]> = { Frontal: [], Central: [], Temporal: [], Parietal: [], Occipital: [] };

    allChannels.forEach((ch) => {
      const regionKey = getRegionFromChannel(ch);
      if (!regions[regionKey]) return;

      const getBandBreakdown = (rowObj: any) => {
        if (!rowObj) return { Delta: 0, Theta: 0, Alpha: 0, Beta: 0, HighBeta: 0, Gamma: 0 };
        let deltaSum = 0, thetaSum = 0, alphaSum = 0, betaSum = 0, hiBetaSum = 0, gammaSum = 0;

        Object.keys(rowObj).forEach((key) => {
          const numericHz = parseInt(key.toLowerCase().replace("hz", ""));
          if (!isNaN(numericHz)) {
            const val = parseFloat(rowObj[key]) || 0;
            if (numericHz >= 1 && numericHz <= 4) deltaSum += val;
            else if (numericHz >= 5 && numericHz <= 8) thetaSum += val;
            else if (numericHz >= 9 && numericHz <= 12) alphaSum += val;
            else if (numericHz >= 13 && numericHz <= 20) betaSum += val;
            else if (numericHz >= 21 && numericHz <= 30) hiBetaSum += val;
            else if (numericHz >= 31 && numericHz <= 45) gammaSum += val;
          }
        });

        const total = deltaSum + thetaSum + alphaSum + betaSum + hiBetaSum + gammaSum;
        if (total === 0) return { Delta: 0, Theta: 0, Alpha: 0, Beta: 0, HighBeta: 0, Gamma: 0 };

        return {
          Delta: (deltaSum / total) * 100, Theta: (thetaSum / total) * 100, Alpha: (alphaSum / total) * 100,
          Beta: (betaSum / total) * 100, HighBeta: (hiBetaSum / total) * 100, Gamma: (gammaSum / total) * 100
        };
      };

      regions[regionKey].push({
        channel: ch, pre: getBandBreakdown(preRel[ch]), post: getBandBreakdown(postRel[ch])
      });
    });
    return regions;
  };

  const handleProcessScans = async (pre = preFile, post = postFile) => {
    if (!pre || !post) {
      setUploadError("Both Pre and Post scan files are mandatory!");
      return;
    }
    setUploadError("");

    try {
      const preParsed = await parseExcelFile(pre);
      const postParsed = await parseExcelFile(post);

      setPreCalculatedData(preParsed);
      setPostCalculatedData(postParsed);

      const prePeakObj = preParsed.alphaPeak || {};
      const postPeakObj = postParsed.alphaPeak || {};
      const uniquePeakChannels = Array.from(new Set([...Object.keys(prePeakObj), ...Object.keys(postPeakObj)]));

      const computedAlphaShifts = uniquePeakChannels
        .map((channel) => {
          const getHzVal = (rowObj: any) => {
            if (!rowObj) return 0;
            const hzKey = Object.keys(rowObj).find(
              (k) => k.toLowerCase().includes("frequency") || k.toLowerCase().includes("val")
            );
            return hzKey ? parseFloat(rowObj[hzKey]) || 0 : 0;
          };

          const beforeVal = getHzVal(prePeakObj[channel]);
          const afterVal = getHzVal(postPeakObj[channel]);
          const baseCoords = brainElectrodesCoords.find((d) => d.channel.toLowerCase() === channel.toLowerCase());

          return {
            channel,
            before: beforeVal,
            after: afterVal,
            shift: afterVal - beforeVal,
            x: baseCoords ? baseCoords.x : 250,
            y: baseCoords ? baseCoords.y : 220,
          };
        })
        .filter((item) => item.before > 0 || item.after > 0);

      setDynamicAlphaPeakData(computedAlphaShifts);

      const preRatiosObj = preParsed.ratios || {};
      const postRatiosObj = postParsed.ratios || {};
      
      const ratioChannelsList = Array.from(new Set([...Object.keys(preRatiosObj), ...Object.keys(postRatiosObj)]));
      const ratioNamesSet = new Set<string>();
      
      ratioChannelsList.forEach(ch => {
        if (preRatiosObj[ch]) {
          Object.keys(preRatiosObj[ch]).forEach(k => {
            if (!k.toLowerCase().startsWith("channel")) ratioNamesSet.add(k);
          });
        }
        if (postRatiosObj[ch]) {
          Object.keys(postRatiosObj[ch]).forEach(k => {
            if (!k.toLowerCase().startsWith("channel")) ratioNamesSet.add(k);
          });
        }
      });

      const computedRatios = Array.from(ratioNamesSet).map(ratioName => {
        const allChannelsData = ratioChannelsList.map(ch => {
          const preVal = parseFloat(preRatiosObj[ch]?.[ratioName]) || 0;
          const postVal = parseFloat(postRatiosObj[ch]?.[ratioName]) || 0;
          const pct = preVal !== 0 ? ((postVal - preVal) / preVal) * 100 : 0;
          return { ch, pct, val: postVal.toFixed(2) };
        });

        const tags = allChannelsData.map(d => ({
          ch: d.ch,
          val: d.val,
          pct: `${d.pct >= 0 ? "+" : ""}${d.pct.toFixed(0)}%`,
          neg: d.pct < 0
        }));

        return { name: ratioName, allChannelsData, tags };
      });
      setDynamicSpectralRatios(computedRatios);

      const computedRelativePower = calculateDynamicRelativeComposition(preParsed, postParsed);
      setDynamicRelativePower(computedRelativePower);

      const availableAbsChannels = Object.keys(preParsed.absolute || {});
      if (availableAbsChannels.length > 0) {
        setAbsolutePowerChannel(availableAbsChannels[0]);
        setPerHzAbsoluteChannel(availableAbsChannels[0]);
      }
      const availableRelChannels = Object.keys(preParsed.relative || {});
      if (availableRelChannels.length > 0) {
        setPerHzRelativeChannel(availableRelChannels[0]);
      }

      setIsProcessed(true);
      setShowUploadPanel(false);

    } catch (error) {
      setUploadError("Error parsing Excel files. Please verify data formats.");
      console.error(error);
    }
  };

  const calculateBandAverage = (dataset: any, sheetKey: 'relative' | 'absolute', channel: string, band: string) => {
    if (!dataset || !dataset[sheetKey] || !dataset[sheetKey][channel]) return 0;
    const rowData = dataset[sheetKey][channel];
    const range = BAND_MAPPING[band];
    if (!range) return 0;
    
    let sum = 0, count = 0;
    for (let hz = range.start; hz <= range.end; hz++) {
      const columnKey = Object.keys(rowData).find(k => k.toLowerCase() === `${hz}hz` || k === String(hz));
      if (columnKey && rowData[columnKey] !== undefined) {
        sum += parseFloat(rowData[columnKey]);
        count++;
      }
    }
    return count > 0 ? sum / count : 0;
  };

  const displayChanges = useMemo(() => {
    if (!isProcessed || !preCalculatedData || !postCalculatedData) return null;
    const compiledList: Array<{ category: string; metric: string; range: string; targetPct: string; color: string }> = [];
    const categories: Array<{ label: string; key: 'relative' | 'absolute' }> = [
      { label: "Relative Power", key: "relative" }, 
      { label: "Absolute Power", key: "absolute" }
    ];

    categories.forEach((cat) => {
      REQUIRED_CHANNELS.forEach((channel) => {
        const preVal = calculateBandAverage(preCalculatedData, cat.key, channel, activeBand);
        const postVal = calculateBandAverage(postCalculatedData, cat.key, channel, activeBand);
        let pctText = "0%";
        if (preVal !== 0) {
          const diffPct = ((postVal - preVal) / preVal) * 100;
          pctText = `${diffPct >= 0 ? "+" : ""}${diffPct.toFixed(1)}%`;
        }
        compiledList.push({
          category: cat.label, metric: `${activeBand} @ {${channel}}`,
          range: `${preVal.toFixed(3)} → ${postVal.toFixed(3)}`, targetPct: pctText,
          color: postVal >= preVal ? "#22c55e" : "#ef4444"
        });
      });
    });
    return compiledList;
  }, [isProcessed, preCalculatedData, postCalculatedData, activeBand]);

  const absolutePowerBrainMapMetrics = useMemo(() => {
    if (!isProcessed || !preCalculatedData || !postCalculatedData) return [];
    
    const preAbs = preCalculatedData.absolute || {};
    const postAbs = postCalculatedData.absolute || {};
    const channelsList = Array.from(new Set([...Object.keys(preAbs), ...Object.keys(postAbs)]));

    return channelsList.map((ch) => {
      const preVal = calculateBandAverage(preCalculatedData, "absolute", ch, activeBand);
      const postVal = calculateBandAverage(postCalculatedData, "absolute", ch, activeBand);
      let pctChange = 0;
      if (preVal !== 0) pctChange = ((postVal - preVal) / preVal) * 100;
      
      const baseCoords = brainElectrodesCoords.find(d => d.channel.toLowerCase() === ch.toLowerCase());
      return {
        channel: ch, preVal, postVal, pctChange,
        x: baseCoords ? baseCoords.x : 250, y: baseCoords ? baseCoords.y : 220
      };
    });
  }, [isProcessed, preCalculatedData, postCalculatedData, activeBand]);

  const dynamicCoherencePairs = useMemo(() => {
    if (!isProcessed || !preCalculatedData || !postCalculatedData) return [];

    const preRows = preCalculatedData.coherence || [];
    const postRows = postCalculatedData.coherence || [];

    const getPairChannels = (row: any) => {
      const keys = Object.keys(row);
      const ch1Key = keys.find(k => ["channel1", "channels1", "ch1", "electrode1"].includes(k.replace(/\s+/g, "").toLowerCase()));
      const ch2Key = keys.find(k => ["channel2", "channels2", "ch2", "electrode2"].includes(k.replace(/\s+/g, "").toLowerCase()));
      return {
        ch1: ch1Key ? String(row[ch1Key]).trim() : null,
        ch2: ch2Key ? String(row[ch2Key]).trim() : null
      };
    };

    const getBandValue = (row: any, band: string) => {
      if (!row) return 0;
      const range = BAND_MAPPING[band];
      if (!range) return 0;
      let sum = 0, count = 0;
      for (let hz = range.start; hz <= range.end; hz++) {
        const columnKey = Object.keys(row).find(k => k.toLowerCase() === `${hz}hz` || k === String(hz));
        if (columnKey && row[columnKey] !== undefined) {
          sum += parseFloat(row[columnKey]) || 0;
          count++;
        }
      }
      return count > 0 ? sum / count : 0;
    };

    const findPostRow = (ch1: string, ch2: string) => postRows.find((r) => {
      const rc = getPairChannels(r);
      return ((rc.ch1 === ch1 && rc.ch2 === ch2) || (rc.ch1 === ch2 && rc.ch2 === ch1));
    });

    const computed = preRows.map((preRow) => {
      const { ch1, ch2 } = getPairChannels(preRow);
      if (!ch1 || !ch2) return null;

      const c1 = brainElectrodesCoords.find(d => d.channel.toLowerCase() === ch1.toLowerCase());
      const c2 = brainElectrodesCoords.find(d => d.channel.toLowerCase() === ch2.toLowerCase());
      if (!c1 || !c2) return null;

      const postRow = findPostRow(ch1, ch2);
      const preVal = getBandValue(preRow, activeBand);
      const postVal = getBandValue(postRow, activeBand);
      let pctChange = 0;
      if (preVal !== 0) pctChange = ((postVal - preVal) / preVal) * 100;

      return {
        pair: `${ch1}-${ch2}`, ch1, ch2, preVal, postVal, pctChange,
        x1: c1.x, y1: c1.y, x2: c2.x, y2: c2.y
      };
    }).filter(Boolean);

    computed.sort((a, b) => {
      const pa = Math.abs(a?.pctChange ?? 0);
      const pb = Math.abs(b?.pctChange ?? 0);
      return pb - pa;
    });
    return computed;
  }, [isProcessed, preCalculatedData, postCalculatedData, activeBand]);

  const flaggedCoherencePairs = useMemo(
    () => dynamicCoherencePairs.filter(p => p && Math.abs(p.pctChange) >= coherenceThreshold),
    [dynamicCoherencePairs, coherenceThreshold]
  );

  const parsedAbsolutePowerChannels = useMemo(() => {
    if (!isProcessed || !preCalculatedData) return [];
    return Object.keys(preCalculatedData.absolute || {});
  }, [isProcessed, preCalculatedData]);

  const parsedRelativePowerChannels = useMemo(() => {
    if (!isProcessed || !preCalculatedData) return [];
    return Object.keys(preCalculatedData.relative || {});
  }, [isProcessed, preCalculatedData]);

  const currentPerHzContext = useMemo(() => {
    const isAbsoluteContext = perHzViewMode === "absolute"; 
    const targetSheetKey = isAbsoluteContext ? "absolute" : "relative";
    const selectedChannel = isAbsoluteContext ? perHzAbsoluteChannel : perHzRelativeChannel;
    const availableChannels = isAbsoluteContext ? parsedAbsolutePowerChannels : parsedRelativePowerChannels;
    return { isAbsoluteContext, targetSheetKey, selectedChannel, availableChannels };
  }, [perHzViewMode, perHzAbsoluteChannel, perHzRelativeChannel, parsedAbsolutePowerChannels, parsedRelativePowerChannels]);

  const dynamicPerHzRows = useMemo(() => {
    if (!isProcessed || !preCalculatedData || !postCalculatedData) return [];
    
    const { targetSheetKey, selectedChannel } = currentPerHzContext;
    const preSheet = preCalculatedData[targetSheetKey as keyof ParsedExcelData] || {};
    const postSheet = postCalculatedData[targetSheetKey as keyof ParsedExcelData] || {};
    
    const preRow = (preSheet as Record<string, any>)[selectedChannel];
    const postRow = (postSheet as Record<string, any>)[selectedChannel];

    const matrixRows: Array<{
      hz: number;
      bandName: string;
      color: string;
      preVal: number;
      postVal: number;
      delta: number;
      pctText: string;
    }> = [];

    for (let hz = 1; hz <= 45; hz++) {
      const matchKey = (row: any) => {
        if (!row) return undefined;
        return Object.keys(row).find(k => k.toLowerCase() === `${hz}hz` || k === String(hz));
      };

      const preKey = matchKey(preRow);
      const postKey = matchKey(postRow);

      const preVal = preKey !== undefined ? parseFloat(preRow[preKey]) || 0 : 0;
      const postVal = postKey !== undefined ? parseFloat(postRow[postKey]) || 0 : 0;
      
      const delta = postVal - preVal;
      let pctChangeText = "0%";
      if (preVal !== 0) {
        const change = (delta / preVal) * 100;
        pctChangeText = `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`;
      }

      let detectedBandName = "Other";
      let bandColor = "#64748b";
      
      Object.keys(BAND_MAPPING).forEach((bName) => {
        const boundary = BAND_MAPPING[bName];
        if (hz >= boundary.start && hz <= boundary.end) {
          detectedBandName = bName;
          bandColor = boundary.color;
        }
      });

      matrixRows.push({
        hz, bandName: detectedBandName, color: bandColor,
        preVal, postVal, delta, pctText: pctChangeText
      });
    }
    return matrixRows;
  }, [isProcessed, preCalculatedData, postCalculatedData, currentPerHzContext]);

  return (
    <Spin spinning={isDownloading} tip="Loading..."> 
    <div className="nrc-container">
      {compareInfo?.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            background: "#f0f7ff",
            border: "1px solid #bae0ff",
            borderRadius: "8px",
            padding: "12px 20px",
            marginBottom: "20px"
          }}>
            {compareInfo.map((item: any, idx: number) => {
              const reqNum = item?.servicerequest_info?.encrypt_RequestNumber || '--';
              const relDate = item?.servicerequest_info?.created_on 
                ? dayjs(item?.servicerequest_info?.created_on).format('MM/DD/YYYY') 
                : '--';
              const label = idx === 0 ? "Pre Evaluation" : "Post Evaluation";

              return (
                <div key={idx} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#0958d9" }}>
                    {label}
                  </span>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#001529" }}>
                    {reqNum}
                  </div>
                  <div style={{ fontSize: "12px", color: "#595959" }}>
                    Released On: <strong style={{ color: "#262626" }}>{relDate}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", marginBottom: "20px" }}>
        <div className="nrc-toggle-wrapper" style={{ margin: 0, display: "flex" }}>
          <button type="button" onClick={() => setEyeState("ec")} className={`nrc-toggle-btn ${eyeState === "ec" ? "nrc-active" : ""}`}>Eyes Closed</button>
          <button type="button" onClick={() => setEyeState("eo")} className={`nrc-toggle-btn ${eyeState === "eo" ? "nrc-active" : ""}`}>Eyes Open</button>
        </div>
        
      </div>

      {showUploadPanel && (
        <div style={{ background: "#f8fafc", border: "2px dashed #cbd5e1", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
          <h4 style={{ margin: "0 0 15px 0", color: "#334155" }}>Upload Evaluation Scans (xlsx workbook sheets)</h4>
          <div style={{ display: "flex", gap: "20px", marginBottom: "15px", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "220px" }}>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px", fontSize: "14px" }}>Pre Scan Excel *</label>
              <input type="file" accept=".xlsx, .xls" onChange={(e) => setPreFile(e.target.files ? e.target.files[0] : null)} style={{ width: "100%", padding: "6px" }} />
            </div>
            <div style={{ flex: 1, minWidth: "220px" }}>
              <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px", fontSize: "14px" }}>Post Scan Excel *</label>
              <input type="file" accept=".xlsx, .xls" onChange={(e) => setPostFile(e.target.files ? e.target.files[0] : null)} style={{ width: "100%", padding: "6px" }} />
            </div>
          </div>
          {uploadError && <div style={{ color: "#ef4444", fontSize: "14px", marginBottom: "10px" }}>{uploadError}</div>}
          <button type="button" onClick={() => handleProcessScans()} style={{ padding: "8px 16px", background: "#10b981", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}>Compare Data Set</button>
        </div>
      )}

      <div className="nrc-ribbon-filter" style={{ position: "sticky", top: 0, zIndex: 100, background: "#fff", marginBottom: "20px", padding: "12px 16px", borderRadius: "8px", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div className="nrc-ribbon-info">
          <span className="nrc-hz-badge">Hz</span>
          <div>
            <div className="nrc-ribbon-title">Frequency band</div>
            <div className="nrc-ribbon-subtitle">Drives topomap, phase coherence & asymmetry</div>
          </div>
        </div>
        <div className="nrc-band-buttons">
          {["Delta", "Theta", "Alpha", "Beta", "High Beta"].map((band) => (
            <button key={band} type="button" onClick={() => setActiveBand(band)} className={`nrc-band-btn ${activeBand === band ? "nrc-active" : ""}`}>{band}</button>
          ))}
        </div>
      </div>

      <div className="nrc-changes-panel" style={{ marginTop: "0", marginBottom: "25px" }}>
        <h5>Biggest changes ({eyeState.toUpperCase()})</h5>
        {!displayChanges ? (
          <div style={{ padding: "30px", textAlign: "center", background: "#fff", borderRadius: "8px", border: "1px dashed #cbd5e1", color: "#64748b" }}>ℹ️ No data available. Please upload Pre and Post scan files.</div>
        ) : (
          <div className="nrc-changes-grid">
            {displayChanges.map((item, idx) => (
              <div key={idx} className="nrc-change-card">
                <div>
                  <div className="nrc-card-category">{item.category}</div>
                  <div className="nrc-card-metric">{item.metric}</div>
                </div>
                <div className="nrc-card-footer">
                  <span className="nrc-card-range">{item.range}</span>
                  <span className="nrc-card-pct" style={{ color: item.color }}>{item.targetPct}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="nrc-section-box" style={{ marginTop: "25px", background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>Alpha peak frequency shift</h2>
            <span style={{ background: "#e0f2fe", color: "#0284c7", fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "12px", border: "1px solid #bae6fd" }}>key metric</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", fontWeight: "600", color: "#475569" }}>
            <span>flag threshold</span>
            <input type="range" min="0.05" max="1.5" step="0.01" value={frequencyThreshold} onChange={(e) => setFrequencyThreshold(Number(e.target.value))} disabled={!isProcessed} style={{ accentColor: "#0284c7", cursor: "pointer" }} />
            <span style={{ minWidth: "80px", textAlign: "right", fontFamily: "monospace" }}>|Δ| ≥ {frequencyThreshold.toFixed(1)} Hz</span>
          </div>
        </div>

        {!isProcessed ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>ℹ️ Brain peak shifts will populate here once both Pre and Post scan spreadsheets are processed.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "400px 1fr", gap: "24px", alignItems: "start" }}>
            <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
              <div style={{ position: "relative", width: "360px", height: "380px", margin: "0 auto" }}>
                <svg viewBox="0 0 360 380" style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
                  <path d="M 32 170 C 15 170 15 210 32 210" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2.5" />
                  <path d="M 32 170 C 15 170 15 210 32 210" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2.5" />
                  <path d="M 328 170 C 345 170 345 210 328 210" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2.5" />
                  <circle cx="180" cy="190" r="148" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2.5" />
                  <path d="M 165 43 L 180 22 L 195 43 Z" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2.5" strokeLinejoin="round" />
                </svg>

                {dynamicAlphaPeakData.map((item) => {
                  const isFlagged = Math.abs(item.shift) >= frequencyThreshold;
                  const coordMap: Record<string, { x: number; y: number }> = {
                    Fp1: { x: 135, y: 75 },  Fp2: { x: 225, y: 75 },
                    F7:  { x: 75,  y: 130 }, F3:  { x: 128, y: 130 }, Fz: { x: 180, y: 130 }, F4: { x: 232, y: 130 }, F8: { x: 285, y: 130 },
                    T3:  { x: 65,  y: 190 }, C3:  { x: 122, y: 190 }, Cz: { x: 180, y: 190 }, C4: { x: 238, y: 190 }, T4: { x: 295, y: 190 },
                    T5:  { x: 80,  y: 250 }, P3:  { x: 128, y: 250 }, Pz: { x: 180, y: 250 }, P4: { x: 232, y: 250 }, T6: { x: 280, y: 250 },
                    O1:  { x: 135, y: 305 }, O2:  { x: 225, y: 305 }
                  };
                  const pos = coordMap[item.channel] || { x: item.x, y: item.y };

                  return (
                    <div key={item.channel} style={{ position: "absolute", left: `${pos.x}px`, top: `${pos.y}px`, transform: "translate(-50%, -50%)", textAlign: "center", zIndex: isFlagged ? 10 : 2 }}>
                      <div className={isFlagged ? "flagged-circle-blink" : ""} style={{ width: "42px", height: "42px", borderRadius: "50%", background: isFlagged ? "rgba(254, 240, 138, 0.45)" : "#ffffff", border: isFlagged ? "2px dashed #eab308" : "1px solid #cbd5e1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", transition: "all 0.25s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                        <span style={{ fontSize: "9px", fontWeight: "700", color: "#475569", lineHeight: "1" }}>{item.channel}</span>
                        <span style={{ fontSize: "9px", fontWeight: "bold", color: "#0f172a", margin: "1px 0" }}>{item.before.toFixed(1)}→{item.after.toFixed(1)}</span>
                        <span style={{ fontSize: "8.5px", fontWeight: "bold", color: item.shift >= 0 ? "#16a34a" : "#dc2626", lineHeight: "1" }}>{item.shift >= 0 ? "▲ +" : "▼ "}{item.shift.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", fontSize: "11px", color: "#64748b", borderTop: "1px solid #e2e8f0", paddingTop: "10px" }}>
                <span>-1.5 Hz</span>
                <div style={{ width: "110px", height: "6px", background: "linear-gradient(to right, #ef4444, #22c55e)", borderRadius: "3px" }} />
                <span>+1.5 Hz</span>
                <span style={{ display: "flex", alignItems: "center", gap: "4px", fontWeight: "600", color: "#a16207" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", border: "2px dashed #eab308", display: "inline-block" }} /> flag |Δ| ≥ {frequencyThreshold.toFixed(1)} Hz
                </span>
              </div>
            </div>

            <div>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#64748b", marginBottom: "12px" }}>Per-channel summary</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", maxHeight: "380px", overflowY: "auto", paddingRight: "4px" }}>
                {dynamicAlphaPeakData.map((item) => {
                  const isFlagged = Math.abs(item.shift) >= frequencyThreshold;
                  return (
                    <div key={item.channel} style={{ background: isFlagged ? "#fefce8" : "#ffffff", border: isFlagged ? "1.5px solid #eab308" : "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", textAlign: "center", boxShadow: isFlagged ? "0 2px 8px rgba(234, 179, 8, 0.12)" : "none", transition: "all 0.2s ease" }}>
                      <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "3px" }}>{item.channel}</div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a", marginBottom: "2px" }}>{item.before.toFixed(2)} → {item.after.toFixed(2)}</div>
                      <div style={{ fontSize: "11px", fontWeight: "700", color: item.shift >= 0 ? "#16a34a" : "#dc2626" }}>{item.shift >= 0 ? "+" : ""}{item.shift.toFixed(2)} Hz</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="nrc-section-box">
        <div className="nrc-section-header-simple" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>Spectral changes</h2>
            <span style={{ background: "#e0f2fe", color: "#0284c7", fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "12px", border: "1px solid #bae6fd" }}>Ratios highlighted</span>
            <span style={{ color: "#64748b", fontSize: "14px" }}>Switch view: Ratios surface the most clinically meaningful shifts. Relative Power shows band composition. Absolute Power keeps the raw scalp map.

</span>
          </div>
        </div>

        <div className="nrc-tabs-row">
          {["ratios", "relative power", "absolute power", "per-hz spectrum"].map((tab) => (
            <button key={tab} type="button" onClick={() => setSpectralTab(tab)} className={`nrc-tab-btn ${spectralTab === tab ? "nrc-active" : ""}`}>{tab.toUpperCase()}</button>
          ))}
        </div>

{spectralTab === "ratios" && (
  <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", padding: "12px 16px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
      <div>
        <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>Ratio change map</h3>
        <span style={{ fontSize: "11px", color: "#64748b" }}>Ranked by total movement. Each head shows all 19 channels for that ratio.</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", fontWeight: "600", color: "#475569" }}>
          <span>flag threshold</span>
          <input type="range" min="5" max="100" step="1" value={ratioThreshold} onChange={(e) => setRatioThreshold(Number(e.target.value))} style={{ accentColor: "#0284c7", cursor: "pointer", width: "110px" }} />
          <span style={{ minWidth: "60px", textAlign: "right", fontFamily: "monospace", color: "#0284c7", fontWeight: "bold" }}>|Δ| ≥ {ratioThreshold}%</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#64748b" }}>
          <span>-</span>
          <div style={{ width: "60px", height: "4px", background: "linear-gradient(to right, #ef4444, #10b981)", borderRadius: "2px" }} />
          <span>+</span>
        </div>
      </div>
    </div>

    {!isProcessed ? (
      <div style={{ padding: "40px", textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>📊 No data loaded. Please run file comparison parser.</div>
    ) : (
      <div className="nrc-ratios-list" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {dynamicSpectralRatios.map((ratio, index) => {
          const currentThreshold = ratioThreshold || 25;
          const flaggedChannelsCount = Array.isArray(ratio.allChannelsData)
            ? ratio.allChannelsData.filter((ch: any) => Math.abs(ch.pct) >= currentThreshold).length
            : 0;

          return (
            <div key={index} className="nrc-ratio-row" style={{ display: "flex", alignItems: "center", gap: "20px", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px 18px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
              <div className="nrc-ratio-meta" style={{ width: "160px", flexShrink: 0 }}>
                <div className="nrc-ratio-name" style={{ fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>{ratio.name}</div>
                <div className="nrc-ratio-count" style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>{flaggedChannelsCount} of 19 channels ≥ {currentThreshold}%</div>
              </div>

              <div style={{ width: "100px", height: "100px", position: "relative", flexShrink: 0 }}>
                <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                  <path d="M 8 45 C 3 45 3 55 8 55" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2.5" />
                  <path d="M 92 45 C 97 45 97 55 92 55" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2.5" />
                  <circle cx="50" cy="50" r="41" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="2" />
                  <path d="M 44 10 L 50 3 L 56 10 Z" fill="#cbd5e1" stroke="#cbd5e1" strokeWidth="1" strokeLinejoin="round" />
                  {(() => {
                    const miniCoords = [
                      { ch: "Fp1", x: 38, y: 18 }, { ch: "Fp2", x: 62, y: 18 },
                      { ch: "F7",  x: 20, y: 34 }, { ch: "F3",  x: 35, y: 34 }, { ch: "Fz", x: 50, y: 34 }, { ch: "F4", x: 65, y: 34 }, { ch: "F8", x: 80, y: 34 },
                      { ch: "T3",  x: 18, y: 50 }, { ch: "C3",  x: 34, y: 50 }, { ch: "Cz", x: 50, y: 50 }, { ch: "C4", x: 66, y: 50 }, { ch: "T4", x: 82, y: 50 },
                      { ch: "T5",  x: 22, y: 66 }, { ch: "P3",  x: 35, y: 66 }, { ch: "Pz", x: 50, y: 66 }, { ch: "P4", x: 65, y: 66 }, { ch: "T6", x: 78, y: 66 },
                      { ch: "O1",  x: 38, y: 82 }, { ch: "O2",  x: 62, y: 82 }
                    ];
                    return miniCoords.map((pt) => {
                      const rawData = ratio.allChannelsData?.find((c: any) => String(c.ch).toLowerCase() === pt.ch.toLowerCase());
                      const pctVal = rawData ? rawData.pct : 0;
                      
                      const isFlagged = Math.abs(pctVal) >= currentThreshold;
                      
                      let dotFill = "#cbd5e1";
                      if (pctVal > 0) dotFill = "#10b981";
                      else if (pctVal < 0) dotFill = "#ef4444";

                      return (
                        <circle 
                          key={pt.ch} 
                          cx={pt.x} 
                          cy={pt.y} 
                          r="4.5" 
                          fill={dotFill} 
                          stroke={isFlagged ? "#eab308" : "#ffffff"} 
                          strokeWidth={isFlagged ? "1.8" : "0.8"} 
                        />
                      );
                    });
                  })()}
                </svg>
              </div>

              <div className="nrc-tags-container" style={{ flex: 1, display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {(ratio.tags || [])
                  .filter((tag: any) => {
                    const numericPct = Math.abs(parseFloat(tag.pct.replace("%", ""))) || 0;
                    return numericPct >= currentThreshold;
                  })
                  .map((tag: any, idx: number) => {
                    const isNeg = tag.neg || String(tag.pct || "").includes("-");
                    return (
                      <span
                        key={idx}
                        style={{
                          padding: "5px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontFamily: "monospace",
                          border: isNeg ? "1px solid #fecaca" : "1px solid #a7f3d0",
                          background: isNeg ? "#fef2f2" : "#f0fdf4",
                          color: isNeg ? "#b91c1c" : "#15803d",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px"
                        }}
                      >
                        <strong>{tag.ch}</strong> {tag.val} <span>{tag.pct}</span>
                      </span>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
)}

        {spectralTab === "relative power" && (
          <div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", background: "#f8fafc", padding: "12px 15px", borderRadius: "6px", marginBottom: "20px", border: "1px solid #e2e8f0" }}>
              <span style={{ fontWeight: "bold", fontSize: "13px", color: "#475569" }}>Bands Color Code:</span>
              {Object.keys(BAND_MAPPING).map(b => (
                <span key={b} style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "5px" }}>
                  <span style={{ display: "inline-block", width: "12px", height: "12px", background: BAND_MAPPING[b].color, borderRadius: "3px" }} />{b}
                </span>
              ))}
            </div>

            {!isProcessed ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>ℹ️ Awaiting upload sheets for relative power generation.</div>
            ) : (
              <div className="nrc-regions-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "25px" }}>
                {Object.keys(dynamicRelativePower).map((region) => {
                  const regionChannels = dynamicRelativePower[region] || [];
                  return (
                    <div key={region} className="nrc-region-card" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "15px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", borderBottom: "1px solid #f1f5f9", paddingBottom: "8px" }}>
                        <span style={{ fontWeight: "bold", fontSize: "16px", color: "#1e293b" }}>{region} Region</span>
                        <span style={{ background: "#ede9fe", color: "#6366f1", fontSize: "12px", padding: "4px 10px", borderRadius: "12px", fontWeight: "bold" }}>{regionChannels.length} Channels</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        {regionChannels.map((item: any) => (
                          <div key={item.channel} style={{ borderBottom: "1px dashed #f1f5f9", paddingBottom: "12px" }}>
                            <div style={{ fontSize: "13px", fontWeight: "bold", color: "#475569", marginBottom: "6px" }}>Channel: <span style={{ color: "#6366f1" }}>{item.channel}</span></div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ width: "30px", fontSize: "11px", color: "#94a3b8", fontWeight: "bold" }}>Pre</span>
                                <div style={{ display: "flex", flex: 1, height: "18px", borderRadius: "4px", overflow: "hidden", background: "#f1f5f9" }}>
                                  {Object.keys(BAND_MAPPING).map((bandKey) => {
                                    const percentVal = item.pre[bandKey.replace(" ", "")] || 0; if (percentVal === 0) return null;
                                    return <div key={bandKey} style={{ width: `${percentVal}%`, background: BAND_MAPPING[bandKey].color, height: "100%" }} title={`Pre [${item.channel}] ${bandKey}: ${percentVal.toFixed(1)}%`} />;
                                  })}
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ width: "30px", fontSize: "11px", color: "#94a3b8", fontWeight: "bold" }}>Post</span>
                                <div style={{ display: "flex", flex: 1, height: "18px", borderRadius: "4px", overflow: "hidden", background: "#f1f5f9" }}>
                                  {Object.keys(BAND_MAPPING).map((bandKey) => {
                                    const percentVal = item.post[bandKey.replace(" ", "")] || 0; if (percentVal === 0) return null;
                                    return <div key={bandKey} style={{ width: `${percentVal}%`, background: BAND_MAPPING[bandKey].color, height: "100%" }} title={`Post [${item.channel}] ${bandKey}: ${percentVal.toFixed(1)}%`} />;
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {spectralTab === "absolute power" && (
          <div>
            {!isProcessed ? (
              <div style={{ padding: "45px", textAlign: "center", background: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1", color: "#64748b" }}>📊 Please upload Pre and Post evaluation sheets to render dynamic absolute power topologies.</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", background: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>Scalp topomap — % change</h3>
                    <div style={{ background: "#e0f2fe", color: "#0369a1", padding: "4px 12px", borderRadius: "16px", fontSize: "12px", fontWeight: "700" }}>Band: {activeBand}</div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "10px 0" }}>
                    <div style={{ position: "relative", width: "320px", height: "340px" }}>
                      <svg viewBox="0 0 320 340" style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
                        <path d="M 28 150 C 12 150 12 190 28 190" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2" />
                        <path d="M 292 150 C 308 150 308 190 292 190" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2" />
                        <circle cx="160" cy="170" r="132" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="2" />
                        <path d="M 148 40 L 160 20 L 172 40 Z" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2" strokeLinejoin="round" />
                      </svg>

                      {absolutePowerBrainMapMetrics.map((node) => {
                        const isSelectedNode = node.channel.toLowerCase() === absolutePowerChannel.toLowerCase();
                        const coordMap: Record<string, { x: number; y: number }> = {
                          Fp1: { x: 122, y: 72 },  Fp2: { x: 198, y: 72 },
                          F7:  { x: 72,  y: 118 }, F3:  { x: 116, y: 118 }, Fz: { x: 160, y: 118 }, F4: { x: 204, y: 118 }, F8: { x: 248, y: 118 },
                          T3:  { x: 62,  y: 170 }, C3:  { x: 111, y: 170 }, Cz: { x: 160, y: 170 }, C4: { x: 209, y: 170 }, T4: { x: 258, y: 170 },
                          T5:  { x: 74,  y: 222 }, P3:  { x: 116, y: 222 }, Pz: { x: 160, y: 222 }, P4: { x: 204, y: 222 }, T6: { x: 246, y: 222 },
                          O1:  { x: 122, y: 268 }, O2:  { x: 198, y: 268 }
                        };
                        const pos = coordMap[node.channel] || { x: node.x, y: node.y };
                        const isPos = node.pctChange >= 0;

                        return (
                          <div key={node.channel} onClick={() => setAbsolutePowerChannel(node.channel)} style={{ position: "absolute", left: `${pos.x}px`, top: `${pos.y}px`, transform: "translate(-50%, -50%)", textAlign: "center", cursor: "pointer", zIndex: isSelectedNode ? 10 : 2 }}>
                            <div style={{ fontSize: "10px", fontWeight: "700", color: "#334155", lineHeight: "1" }}>{node.channel}</div>
                            <div style={{ background: isPos ? "#dcfce7" : "#fee2e2", color: isPos ? "#15803d" : "#b91c1c", border: isSelectedNode ? "2px solid #0284c7" : (isPos ? "1px solid #86efac" : "1px solid #fca5a5"), borderRadius: "10px", padding: "1px 5px", fontSize: "10px", fontWeight: "700", marginTop: "1px", boxShadow: isSelectedNode ? "0 0 0 2px rgba(2, 132, 199, 0.25)" : "none" }}>
                              {isPos ? "+" : ""}{node.pctChange.toFixed(0)}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontSize: "11px", color: "#64748b" }}>
                    <span>Decrease</span>
                    <div style={{ width: "100px", height: "5px", background: "linear-gradient(to right, #ef4444, #10b981)", borderRadius: "3px" }} />
                    <span>Increase</span>
                  </div>
                </div>

                <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>Bands at</span>
                        <select value={absolutePowerChannel} onChange={(e) => setAbsolutePowerChannel(e.target.value)} style={{ padding: "4px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#f8fafc", fontWeight: "700", color: "#0f172a", fontSize: "14px", cursor: "pointer" }}>
                          {parsedAbsolutePowerChannels.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>

                    {(() => {
                      const preRow = preCalculatedData?.absolute?.[absolutePowerChannel];
                      const postRow = postCalculatedData?.absolute?.[absolutePowerChannel];
                      const bandsList = [
                        { name: "Delta", bandKey: "Delta", colorPre: "#6366f1", colorPost: "#818cf8" },
                        { name: "Theta", bandKey: "Theta", colorPre: "#0284c7", colorPost: "#38bdf8" },
                        { name: "Alpha", bandKey: "Alpha", colorPre: "#10b981", colorPost: "#34d399" },
                        { name: "Beta", bandKey: "Beta", colorPre: "#eab308", colorPost: "#fde047" },
                        { name: "HiBeta", bandKey: "High Beta", colorPre: "#f97316", colorPost: "#fb923c" },
                      ];

                      const extractBandValAvg = (rowObj: any, bandName: string) => {
                        if (!rowObj) return 0;
                        const directKey = Object.keys(rowObj).find(k => k.toLowerCase().trim() === bandName.toLowerCase().trim() || k.toLowerCase().trim() === bandName.replace(" ", "").toLowerCase().trim());
                        if (directKey && rowObj[directKey] !== undefined) return parseFloat(rowObj[directKey]) || 0;
                        const range = BAND_MAPPING[bandName];
                        if (!range) return 0;
                        let sum = 0, count = 0;
                        for (let hz = range.start; hz <= range.end; hz++) {
                          const hzKey = Object.keys(rowObj).find(k => k.toLowerCase().replace(/\s+/g, "").replace("hz", "") === String(hz));
                          if (hzKey && rowObj[hzKey] !== undefined) { sum += parseFloat(rowObj[hzKey]) || 0; count++; }
                        }
                        return count > 0 ? sum / count : 0;
                      };

                      return (
                        <div style={{ height: "240px", display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: "0 10px 10px 10px", borderBottom: "1px solid #e2e8f0" }}>
                          {bandsList.map((b) => {
                            const preVal = extractBandValAvg(preRow, b.bandKey);
                            const postVal = extractBandValAvg(postRow, b.bandKey);
                            const shiftPct = preVal > 0 ? (((postVal - preVal) / preVal) * 100) : 0;
                            const calcHeight = (val: number) => {
                              if (val <= 0) return 8;
                              return Math.min(Math.max(Math.log10(val + 1) * 42, 10), 145);
                            };

                            return (
                              <div key={b.name} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "flex-end", gap: "3px", height: "160px" }}>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <span style={{ fontSize: "9px", fontWeight: "700", color: "#64748b", marginBottom: "2px" }}>{preVal.toFixed(2)}</span>
                                    <div style={{ width: "15px", height: `${calcHeight(preVal)}px`, background: b.colorPre, borderRadius: "4px 4px 0 0" }} />
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <span style={{ fontSize: "9px", fontWeight: "700", color: "#64748b", marginBottom: "2px" }}>{postVal.toFixed(2)}</span>
                                    <div style={{ width: "15px", height: `${calcHeight(postVal)}px`, background: b.colorPost, borderRadius: "4px 4px 0 0" }} />
                                  </div>
                                </div>
                                <div style={{ marginTop: "10px", textAlign: "center" }}>
                                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a" }}>{b.name}</div>
                                  <div style={{ fontSize: "11px", fontWeight: "700", color: shiftPct < 0 ? "#dc2626" : "#16a34a", marginTop: "2px" }}>
                                    {shiftPct >= 0 ? "+" : ""}{shiftPct.toFixed(0)}%
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {spectralTab === "per-hz spectrum" && (
          <div>
            <div className="nrc-sub-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 20px", borderRadius: "8px 8px 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontWeight: "600", color: "#000000", fontSize: "15px" }}>Per-Hz % change @</span>
                <select value={perHzViewMode === "absolute" ? perHzAbsoluteChannel : perHzRelativeChannel} onChange={(e) => { if (perHzViewMode === "absolute") { setPerHzAbsoluteChannel(e.target.value); } else { setPerHzRelativeChannel(e.target.value); } }} style={{ padding: "6px 16px", borderRadius: "6px", border: "1px solid #161616", background: "transparent", color: "#070707", fontWeight: "bold", cursor: "pointer", outline: "none" }}>
                  {(perHzViewMode === "absolute" ? parsedAbsolutePowerChannels : parsedRelativePowerChannels).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div style={{ display: "flex", background: "#F1F5F9", padding: "4px", borderRadius: "8px", gap: "4px" }}>
                <button type="button" onClick={() => setPerHzViewMode("absolute")} style={{ padding: "6px 14px", borderRadius: "6px", border: "none", fontSize: "12px", fontWeight: "bold" , cursor: "pointer", background: perHzViewMode === "absolute" ? "#5C59E7" : "transparent", color: perHzViewMode === "absolute" ? "#fff" : "#9ca3af" }}>Absolute</button>
                <button type="button" onClick={() => setPerHzViewMode("relative")} style={{ padding: "6px 14px", borderRadius: "6px", border: "none", fontSize: "12px", fontWeight: "bold", cursor: "pointer", background: perHzViewMode === "relative" ? "#5C59E7" : "transparent", color: perHzViewMode === "relative" ? "#fff" : "#9ca3af" }}>Relative</button>
              </div>
            </div>

            {!isProcessed ? (
              <div style={{ padding: "45px", textAlign: "center", color: "#9ca3af", background: "#1f2937", borderRadius: "0 0 8px 8px" }}> Bars Please upload sheets to aggregate the complete 1Hz through 45Hz table matrix.</div>
            ) : (
              <div className="nrc-table-wrapper" style={{ maxHeight: "550px", overflowY: "auto", border: "1px solid #1f2937", borderRadius: "0 0 8px 8px", background: "#111827" }}>
                <table className="nrc-hz-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", color: "#e5e7eb" }}>
                  <thead style={{ position: "sticky", top: 0, background: "#1f2937", zIndex: 5 }}>
                    <tr style={{ textAlign: "left", borderBottom: "2px solid #374151" }}>
                      <th style={{ padding: "12px", color: "#9ca3af" }}>Hz</th>
                      <th style={{ padding: "12px", color: "#9ca3af" }}>Band</th>
                      <th style={{ padding: "12px", color: "#9ca3af" }}>Pre</th>
                      <th style={{ padding: "12px", color: "#9ca3af" }}>Post</th>
                      <th style={{ padding: "12px", color: "#9ca3af" }}>Δ</th>
                      <th style={{ padding: "12px", textAlign: "right", color: "#9ca3af" }}>% change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dynamicPerHzRows.map((row) => (
                      <tr key={row.hz} style={{ borderBottom: "1px solid #1f2937" }}>
                        <td style={{ padding: "12px", fontWeight: "bold" }}>{row.hz}</td>
                        <td style={{ padding: "12px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: row.color, fontWeight: "600" }}>
                            <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "50%", background: row.color }} />{row.bandName}
                          </span>
                        </td>
                        <td style={{ padding: "12px", fontFamily: "monospace", color: "#9ca3af" }}>{row.preVal.toFixed(2)}</td>
                        <td style={{ padding: "12px", fontFamily: "monospace", color: "#9ca3af" }}>{row.postVal.toFixed(2)}</td>
                        <td style={{ padding: "12px", fontFamily: "monospace", color: "#9ca3af" }}>{row.delta.toFixed(2)}</td>
                        <td style={{ padding: "12px", textAlign: "right", fontWeight: "bold", color: row.pctText.includes("-") ? "#ef4444" : "#22c55e" }}>
                          {row.pctText === "0%" ? "0%" : row.pctText.replace("+", "")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="nrc-section-box" style={{ marginTop: "25px", background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a", marginBottom: "8px" }}>Phase coherence Δ — {activeBand}</h2>
        <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>Each arc connects two electrodes and shows how their pairwise coupling changed (post − pre). Green arcs = increase, red = decrease. Thicker arcs = larger change.</div>

        {!isProcessed ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>ℹ dependency metrics aggregate once processed.</div>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "20px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "14px 18px", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 260px" }}>
                <label style={{ fontSize: "13px", fontWeight: "bold", color: "#475569" }}>Threshold</label>
                <input type="range" min="0" max="100" step="1" value={coherenceThreshold} onChange={(e) => setCoherenceThreshold(Number(e.target.value))} style={{ flex: 1, accentColor: "#6366f1", cursor: "pointer" }} />
                <span style={{ fontSize: "13px", fontWeight: "bold", color: "#1e293b" }}>±{coherenceThreshold}%</span>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#475569", cursor: "pointer" }}>
                <input type="checkbox" checked={showAllCoherencePairs} onChange={(e) => setShowAllCoherencePairs(e.target.checked)} /> show all pairs
              </label>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "400px 1fr", gap: "24px", alignItems: "start" }}>
              <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
                <div style={{ position: "relative", width: "360px", height: "380px", margin: "0 auto" }}>
                  <svg viewBox="0 0 360 380" style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
                    <path d="M 32 170 C 15 170 15 210 32 210" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2.5" />
                    <path d="M 328 170 C 345 170 345 210 328 210" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2.5" />
                    <circle cx="180" cy="190" r="148" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2.5" />
                    <path d="M 165 43 L 180 22 L 195 43 Z" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2.5" strokeLinejoin="round" />
                    {(() => {
                      const getSvgCoords = (chName: string) => {
                        const coordMap: Record<string, { x: number; y: number }> = {
                          Fp1: { x: 135, y: 75 },  Fp2: { x: 225, y: 75 },
                          F7:  { x: 75,  y: 130 }, F3:  { x: 128, y: 130 }, Fz: { x: 180, y: 130 }, F4: { x: 232, y: 130 }, F8: { x: 285, y: 130 },
                          T3:  { x: 65,  y: 190 }, C3:  { x: 122, y: 190 }, Cz: { x: 180, y: 190 }, C4: { x: 238, y: 190 }, T4: { x: 295, y: 190 },
                          T5:  { x: 80,  y: 250 }, P3:  { x: 128, y: 250 }, Pz: { x: 180, y: 250 }, P4: { x: 232, y: 250 }, T6: { x: 280, y: 250 },
                          O1:  { x: 135, y: 305 }, O2:  { x: 225, y: 305 }
                        };
                        return coordMap[chName] || { x: 180, y: 190 };
                      };

                      const calculateArcPath = (ch1: string, ch2: string) => {
                        const p1 = getSvgCoords(ch1); const p2 = getSvgCoords(ch2);
                        const dx = p2.x - p1.x; const dy = p2.y - p1.y;
                        const distance = Math.sqrt(dx * dx + dy * dy); if (distance === 0) return "";
                        const midX = (p1.x + p2.x) / 2; const midY = (p1.y + p2.y) / 2;
                        const nx = -dy / distance; const ny = dx / distance;
                        const curve = Math.min(distance * 0.35, 80);
                        return `M ${p1.x} ${p1.y} Q ${midX + nx * curve} ${midY + ny * curve} ${p2.x} ${p2.y}`;
                      };

                      return (
                        <g>
                          {showAllCoherencePairs && dynamicCoherencePairs.map((p: any, idx) => {
                            if (!p || Math.abs(p.pctChange) >= coherenceThreshold) return null;
                            return <path key={`faded-${idx}`} d={calculateArcPath(p.ch1, p.ch2)} stroke="#cbd5e1" strokeWidth="1" fill="none" opacity="0.4" />;
                          })}
                          {flaggedCoherencePairs.map((p: any, idx) => {
                            if (!p) return null;
                            const isSelected = selectedCoherencePair === p.pair;
                            return <path key={p.pair + idx} d={calculateArcPath(p.ch1, p.ch2)} stroke={p.pctChange >= 0 ? "#22c55e" : "#ef4444"} strokeWidth={isSelected ? 5 : 3} fill="none" opacity={isSelected ? 1 : 0.8} onClick={() => setSelectedCoherencePair(isSelected ? null : p.pair)} style={{ cursor: "pointer" }} />;
                          })}
                          {brainElectrodesCoords.map((item) => {
                            const pos = getSvgCoords(item.channel);
                            return (
                              <g key={item.channel}>
                                <circle cx={pos.x} cy={pos.y} r="13" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" />
                                <text x={pos.x} y={pos.y + 3.5} textAnchor="middle" fontSize="9.5" fontWeight="bold" fill="#1e293b">{item.channel}</text>
                              </g>
                            );
                          })}
                        </g>
                      );
                    })()}
                  </svg>
                </div>
              </div>

              <div>
                <div style={{ maxHeight: "380px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {flaggedCoherencePairs.map((p: any) => {
                    const isSelected = selectedCoherencePair === p.pair;
                    return (
                      <div key={p.pair} onClick={() => setSelectedCoherencePair(isSelected ? null : p.pair)} style={{ display: "flex", alignItems: "center", gap: "10px", background: isSelected ? "#f0fdf4" : "#ffffff", border: isSelected ? "1px solid #22c55e" : "1px solid #e2e8f0", padding: "8px 12px", borderRadius: "6px", cursor: "pointer" }}>
                        <span style={{ width: "70px", fontWeight: "bold", fontSize: "12px" }}>{p.pair}</span>
                        <span style={{ fontSize: "12px", fontWeight: "bold", color: p.pctChange >= 0 ? "#16a34a" : "#dc2626" }}>{p.pctChange >= 0 ? "+" : ""}{p.pctChange.toFixed(0)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

<div className="nrc-section-box" style={{ marginTop: "25px", marginBottom: "40px", backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
    <div>
      <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>Amplitude asymmetry Δ — {activeBand}</h2>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", fontWeight: "600", color: "#475569" }}>
      <span>flag threshold</span>
      <input type="range" min="0" max="100" value={flagThreshold} onChange={(e) => setFlagThreshold(Number(e.target.value))} style={{ accentColor: "#0284c7", cursor: "pointer" }} />
      <span style={{ minWidth: "80px", textAlign: "right", color: "#0284c7", fontWeight: "bold" }}>|Δ| ≥ {flagThreshold}%</span>
    </div>
  </div>

  <div style={{ maxHeight: "480px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px", paddingRight: "4px" }}>
    {!isProcessed ? (
      <div style={{ padding: "30px", textAlign: "center", color: "#94a3b8" }}>Awaiting scan files to calculate asymmetry.</div>
    ) : (
      rawAmplitudeAsymmetry.map((p, idx) => {
        const isFlagged = p.pctChange >= flagThreshold;
        const flipped = isFlipped(p.pre, p.post);
        const isMoreBalanced = Math.abs(p.post) < Math.abs(p.pre);
        
        const color = flipped ? "#9333ea" : (isMoreBalanced ? "#16a34a" : "#dc2626");
        
        const leftVal = Math.min(p.pre, p.post);
        const rightVal = Math.max(p.pre, p.post);
        
        const leftPos = `calc(50% + (${leftVal * 50}%))`;
        const rightPos = `calc(50% + (${rightVal * 50}%))`;
        const prePos = `calc(50% + (${p.pre * 50}%))`;
        const postPos = `calc(50% + (${p.post * 50}%))`;

        return (
          <div 
            key={idx} 
            style={{ 
              display: "grid", 
              gridTemplateColumns: "100px 1fr 140px", 
              gap: "20px", 
              alignItems: "center", 
              backgroundColor: "transparent", 
              padding: "10px 12px", 
              borderBottom: "1px solid #f1f5f9" 
            }}
          >
            <div style={{ fontWeight: "600", fontSize: "13px", color: "#334155" }}>{p.pair}</div>
            
            <div style={{ position: "relative", display: "flex", alignItems: "center", height: "24px" }}>
              <div style={{ position: "absolute", left: "0", right: "52%", height: "8px", backgroundColor: "#f1f5f9", borderRadius: "4px 0 0 4px" }} />
              <div style={{ position: "absolute", left: "52%", right: "0", height: "8px", backgroundColor: "#f1f5f9", borderRadius: "0 4px 4px 0" }} />
              
              <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", width: "2px", height: "14px", backgroundColor: "#cbd5e1" }} />
              
              {isFlagged && (
                <div 
                  style={{ 
                    position: "absolute", 
                    left: leftPos, 
                    right: `calc(100% - ${rightPos})`, 
                    height: "3px", 
                    backgroundColor: color,
                    zIndex: 2,
                    borderRadius: "2px"
                  }} 
                />
              )}

              <div 
                style={{ 
                  position: "absolute", 
                  left: prePos, 
                  transform: "translateX(-50%)",
                  width: "8px", 
                  height: "8px", 
                  borderRadius: "50%", 
                  border: `2px solid ${color}`,
                  backgroundColor: "#ffffff",
                  zIndex: 3 
                }} 
              />
              
              <div 
                style={{ 
                  position: "absolute", 
                  left: postPos, 
                  transform: "translateX(-50%)",
                  width: "10px", 
                  height: "10px", 
                  borderRadius: "50%", 
                  backgroundColor: color,
                  zIndex: 4
                }} 
              />
            </div>
            
             <div style={{ textAlign: "right", fontSize: "12px", fontFamily: "monospace", display: "flex", justifyContent: "flex-end", gap: "10px", alignItems: "center" }}>
              <span style={{ color: "#64748b" }}>{p.pre.toFixed(2)} → {p.post.toFixed(2)}</span>
              <span style={{ fontWeight: "700", color: color, minWidth: "50px" }}>{p.delta > 0 ? "+" : ""}{p.delta.toFixed(2)}</span>
              {flipped && (
                <span style={{ background: "#f3e8ff", color: "#9333ea", fontSize: "9px", fontWeight: "bold", padding: "1px 5px", borderRadius: "4px" }}>FLIP</span>
              )}
            </div>
          </div>
        );
      })
    )}
  </div>
</div>
    </div>
    </Spin>
  );
};

export default NewResultComparison;